import axiosAPI from '../../utils/client/axiosAPI';
import { CartResponse, CartProductResponse } from './contracts';
import { Media } from '../Media/Media';
import { MediaResponse } from '../Media/contracts';

export class CartProduct {
  public readonly id: number;
  public readonly name: string;
  public readonly sku: string;
  public readonly images: string[];
  public readonly price: number;
  public quantity: number;

  public constructor(data: CartProductResponse) {
    this.id = data.id;
    this.name = data.name;
    this.sku = data.sku;
    this.images = data.images;
    this.price = data.price;
    this.quantity = data.quantity;
  }

  /**
   * Correctly resolves the full URL for the product's main image.
   * This ensures that even if the API returns a relative path, the full
   * URL is constructed for use in `<img>` tags.
   */
  public getMainImageUrl(): string | null {
    if (!this.images || this.images.length === 0) {
      return null;
    }

    const imageUrl = this.images[0];

    // If the URL is already absolute, return it directly.
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }

    // Otherwise, create a temporary Media instance to get the full URL.
    // This leverages your existing Media model's logic.
    const mediaData: MediaResponse = {
      id: 0,
      name: this.name,
      path: imageUrl,
      url: imageUrl
    };
    const media = new Media(mediaData);
    return media.fullUrl;
  }
}

export class Cart {
  public readonly id: string;
  public readonly token: string;
  public readonly products: CartProduct[];
  public readonly startDate: string | null;
  public readonly endDate: string | null;

  // All calculated fields are now properties from the backend
  public readonly numberOfDays: number;
  public readonly priceFactor: number;
  public readonly subtotal: number;
  public readonly totalWithFactor: number;

  public readonly createdAt: string;
  public readonly updatedAt: string;
  public readonly userId: number | null;

  constructor(data: CartResponse) {
    this.id = data.id;
    this.token = data.token;
    this.products = data.products.map((p) => new CartProduct(p));
    this.startDate = data.start_date;
    this.endDate = data.end_date;

    // Assign calculated values from the API response
    this.numberOfDays = data.number_of_days;
    this.priceFactor = data.price_factor;
    this.subtotal = data.subtotal;
    this.totalWithFactor = data.total_with_factor;

    this.createdAt = data.created_at;
    this.updatedAt = data.updated_at;
    this.userId = data.user_id;
  }

  // --- API Methods now align with the latest backend routes ---

  public static async get(token: string | null): Promise<Cart> {
    const url = token ? `/carts/${token}` : '/carts';
    const { data } = await axiosAPI.get<CartResponse>(url);
    return new Cart(data);
  }

  public async setStartEndDates(
    startDate: string,
    endDate: string
  ): Promise<Cart> {
    const { data } = await axiosAPI.put<CartResponse>(
      `/carts/${this.token}/dates`,
      {
        start_date: startDate,
        end_date: endDate
      }
    );
    return new Cart(data);
  }

  public async addProduct(productId: number, quantity: number): Promise<Cart> {
    const { data } = await axiosAPI.post<CartResponse>(
      `/carts/${this.token}/products`,
      {
        product_id: productId,
        quantity
      }
    );
    return new Cart(data);
  }

  public async removeProduct(productId: number): Promise<Cart> {
    const { data } = await axiosAPI.delete<CartResponse>(
      `/carts/${this.token}/products/${productId}`
    );
    return new Cart(data);
  }

  public async changeProductQuantity(
    productId: number,
    quantity: number
  ): Promise<Cart> {
    const { data } = await axiosAPI.put<CartResponse>(
      `/carts/${this.token}/products/${productId}`,
      {
        quantity
      }
    );
    return new Cart(data);
  }

  public async emptyCart(): Promise<Cart> {
    const { data } = await axiosAPI.delete<CartResponse>(
      `/carts/${this.token}/products`
    );
    return new Cart(data);
  }

  public hasStartEndDates(): boolean {
    return !!this.startDate && !!this.endDate;
  }
}
